import React, { useState, useEffect } from 'react';
import { Zap, ZapOff, AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Read-only parent-facing banner that surfaces a lightning / weather delay
 * declared by the coach. Shows live countdown when status='delayed'.
 *
 * Pass the game.lightning_delay object directly. Renders nothing if null/empty.
 */
const formatRemaining = (sec) => {
  if (sec <= 0) return 'any moment';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return `${h}h ${rm}m`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
};

export default function LightningDelayBanner({ lightningDelay, opponentName, className = '' }) {
  const [remaining, setRemaining] = useState(0);

  const isDelayed = !!lightningDelay && lightningDelay.status === 'delayed';
  const isCanceled = !!lightningDelay && lightningDelay.status === 'canceled';
  const resumeAt = isDelayed ? lightningDelay.resume_at : null;

  useEffect(() => {
    if (!resumeAt) { setRemaining(0); return undefined; }
    const tick = () => {
      const end = new Date(resumeAt).getTime();
      setRemaining(Math.max(0, Math.round((end - Date.now()) / 1000)));
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [resumeAt]);

  if (!lightningDelay) return null;

  if (isCanceled) {
    return (
      <div
        className={cn(
          'relative bg-stone-900 border-2 border-amber-500 rounded-2xl p-4 shadow-2xl',
          className,
        )}
        data-testid="lightning-banner-canceled"
        role="alert"
      >
        <div className="flex items-center gap-3">
          <ZapOff size={28} className="text-amber-400 shrink-0" />
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-amber-300 font-bold">Game called by coach</p>
            <p className="text-base font-extrabold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Today's game vs {opponentName || 'opponent'} is canceled.
            </p>
            {lightningDelay.note && (
              <p className="text-xs text-stone-300 italic mt-1">&ldquo;{lightningDelay.note}&rdquo;</p>
            )}
            <p className="text-[11px] text-stone-400 mt-1">Coach will message about rescheduling.</p>
          </div>
        </div>
      </div>
    );
  }

  // Delayed
  return (
    <div
      className={cn(
        'relative bg-gradient-to-br from-amber-500 via-amber-400 to-yellow-300 border-2 border-amber-200 rounded-2xl p-4 shadow-2xl',
        className,
      )}
      data-testid="lightning-banner-delayed"
      role="alert"
    >
      <div className="flex items-center gap-3">
        <div className="relative shrink-0">
          <Zap size={36} className="text-stone-900 animate-pulse" fill="currentColor" />
          <AlertTriangle size={14} className="absolute -top-1 -right-1 text-red-700 bg-amber-50 rounded-full p-0.5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-wider text-red-900 font-extrabold">⚡ Lightning Delay</p>
          <p className="text-lg md:text-xl font-extrabold text-stone-900 leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Game vs {opponentName || 'opponent'} paused
          </p>
          <p className="text-sm text-stone-900 mt-0.5">
            Targeting resume in <span className="font-extrabold tabular-nums" data-testid="lightning-banner-countdown">{formatRemaining(remaining)}</span>
            {resumeAt && (
              <span className="text-stone-700">
                {' '}· ≈ {new Date(resumeAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
              </span>
            )}
          </p>
          {lightningDelay.note && (
            <p className="text-xs italic text-stone-800 mt-1">&ldquo;{lightningDelay.note}&rdquo;</p>
          )}
        </div>
      </div>
      {remaining === 0 && resumeAt && (
        <p className="text-[11px] text-red-900 font-bold mt-2 text-center bg-amber-50/60 rounded py-1.5">
          🟢 Resume time reached — keep an eye out for the coach's all-clear
        </p>
      )}
    </div>
  );
}
